// Kevyt toast-järjestelmä — käytä useToast()-hookilla missä tahansa.
// Korvaa hiljaiset .catch(() => {}) -kutsut: kerrotaan käyttäjälle kun jokin
// menee pieleen sen sijaan että näkymä jäisi tyhjäksi.
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';

type ToastKind = 'success' | 'error' | 'info' | 'warning';

interface ToastItem {
  id: number;
  kind: ToastKind;
  message: string;
}

interface ToastContextValue {
  show: (message: string, kind?: ToastKind) => void;
  success: (message: string) => void;
  error: (message: string) => void;
  info: (message: string) => void;
  warning: (message: string) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

// Fallback: jos provider puuttuu, älä kaadu — logaa konsoliin. Moduulitason
// vakio jotta identiteetti pysyy (sama syy kuin providerin useMemo).
const FALLBACK_TOAST: ToastContextValue = {
  show: (m, k) => console.log(`[toast:${k ?? 'info'}]`, m),
  success: m => console.log('[toast:success]', m),
  error: m => console.error('[toast:error]', m),
  info: m => console.log('[toast:info]', m),
  warning: m => console.warn('[toast:warning]', m),
};

export function useToast(): ToastContextValue {
  return useContext(ToastContext) ?? FALLBACK_TOAST;
}

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const idRef = useRef(0);

  const show = useCallback((message: string, kind: ToastKind = 'info') => {
    const id = ++idRef.current;
    setToasts(prev => [...prev, { id, kind, message }]);
    setTimeout(() => {
      setToasts(prev => prev.filter(t => t.id !== id));
    }, kind === 'error' ? 6000 : 3500);
  }, []);

  // Memoizoitu: value pysyy identtisenä renderien yli → useToast()-paluuarvon
  // voi turvallisesti laittaa useCallback/useEffect-depsiin ilman että lataus-
  // callbackit ja intervallit resetoituvat aina kun jokin toast näkyy/poistuu.
  const value: ToastContextValue = useMemo(() => ({
    show,
    success: m => show(m, 'success'),
    error: m => show(m, 'error'),
    info: m => show(m, 'info'),
    warning: m => show(m, 'warning'),
  }), [show]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      <ToastViewport toasts={toasts} onDismiss={id => setToasts(prev => prev.filter(t => t.id !== id))} />
    </ToastContext.Provider>
  );
}

function ToastViewport({ toasts, onDismiss }: { toasts: ToastItem[]; onDismiss: (id: number) => void }) {
  return (
    <div style={{
      position: 'fixed',
      bottom: 16,
      left: '50%',
      transform: 'translateX(-50%)',
      zIndex: 9999,
      display: 'flex',
      flexDirection: 'column',
      gap: 8,
      pointerEvents: 'none',
      maxWidth: 420,
      width: 'calc(100% - 32px)',
    }}>
      {toasts.map(t => <ToastBubble key={t.id} toast={t} onDismiss={() => onDismiss(t.id)} />)}
    </div>
  );
}

function ToastBubble({ toast, onDismiss }: { toast: ToastItem; onDismiss: () => void }) {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setVisible(true), 10);
    return () => clearTimeout(t);
  }, []);

  const colors: Record<ToastKind, { bg: string; bd: string; ic: string }> = {
    success: { bg: '#ecfdf5', bd: '#10b981', ic: '✓' },
    error:   { bg: '#fef2f2', bd: '#dc2626', ic: '⚠' },
    info:    { bg: '#eff6ff', bd: '#2563eb', ic: 'ℹ' },
    warning: { bg: '#fffbeb', bd: '#d97706', ic: '!' },
  };
  const c = colors[toast.kind];

  return (
    <div onClick={onDismiss} style={{
      pointerEvents: 'auto',
      display: 'flex',
      gap: 10,
      padding: '12px 14px',
      background: c.bg,
      borderLeft: `4px solid ${c.bd}`,
      borderRadius: 8,
      boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
      fontSize: 14,
      lineHeight: 1.4,
      color: '#0f172a',
      fontFamily: "var(--font-ui)",
      cursor: 'pointer',
      transform: visible ? 'translateY(0)' : 'translateY(20px)',
      opacity: visible ? 1 : 0,
      transition: 'transform 0.2s ease-out, opacity 0.2s ease-out',
    }}>
      <span style={{ fontSize: 18, color: c.bd, fontWeight: 700, lineHeight: 1 }}>{c.ic}</span>
      <span style={{ flex: 1 }}>{toast.message}</span>
      <span style={{ fontSize: 16, color: '#94a3b8', cursor: 'pointer' }}>×</span>
    </div>
  );
}
